import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { getSettings, sendEnquiry } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [contact, setContact] = useState(null);

  useEffect(() => {
    getSettings()
      .then((s) => setContact(s))
      .catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await sendEnquiry({ name: "Newsletter subscriber", email, subject: "Newsletter", message: "Please add me to the Classina newsletter." });
      toast.success("You're on the list — welcome to Classina");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const address = contact?.contact_address || "230/4 Nana Peth, Pragati Villa, Pune 411002";
  const phone = contact?.contact_phone || "+91 99607 49074";
  const mail = contact?.contact_email || "info@sbkmanufacturing.com";

  return (
    <footer className="bg-foreground text-background py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="inline-block bg-white rounded-xl px-4 py-2.5">
              <img src="/classina-logo.png" alt="Classina Products" className="h-10 w-auto" />
            </div>
            <p className="mt-5 text-sm opacity-70 leading-relaxed max-w-sm">
              Premium, eco-conscious stationery crafted by SBK Manufacturing Co.
              Write Green, Live Clean.
            </p>
            <form onSubmit={subscribe} className="mt-8 flex gap-2 max-w-sm">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email for new launches"
                className="flex-1 rounded-full bg-white/10 border border-white/15 px-5 py-2.5 text-sm outline-none placeholder:opacity-50 focus:ring-2 focus:ring-primary transition"
              />
              <button
                data-testid="newsletter-subscribe-button"
                disabled={saving}
                className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-bold hover:brightness-110 transition disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join"}
              </button>
            </form>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] font-bold opacity-50 mb-5">Shop</p>
            <ul className="space-y-3 text-sm">
              <li><a data-testid="footer-shop-eco" href="#shop" className="opacity-70 hover:opacity-100 transition-opacity">Eco Series</a></li>
              <li><a data-testid="footer-shop-creative" href="#shop" className="opacity-70 hover:opacity-100 transition-opacity">Creative</a></li>
              <li><a data-testid="footer-shop-school" href="#shop" className="opacity-70 hover:opacity-100 transition-opacity">School & College</a></li>
              <li><a data-testid="footer-shop-office" href="#shop" className="opacity-70 hover:opacity-100 transition-opacity">Office & Filing</a></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] font-bold opacity-50 mb-5">Company</p>
            <ul className="space-y-3 text-sm">
              <li><a data-testid="footer-trade" href="#trade" className="opacity-70 hover:opacity-100 transition-opacity">Trade & bulk</a></li>
              <li><a data-testid="footer-contact" href="#contact" className="opacity-70 hover:opacity-100 transition-opacity">Contact</a></li>
              <li><a data-testid="footer-track" href="/track" className="opacity-70 hover:opacity-100 transition-opacity">Track your order</a></li>
              <li><a data-testid="footer-policies" href="/policies" className="opacity-70 hover:opacity-100 transition-opacity">Policies & FAQ</a></li>
              <li><a data-testid="footer-sbk" href="https://www.sbkmanufacturing.com" target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 transition-opacity">SBK Manufacturing</a></li>
              <li><a data-testid="footer-admin" href="/admin" className="opacity-70 hover:opacity-100 transition-opacity">Store admin</a></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] font-bold opacity-50 mb-5">Reach us</p>
            <ul className="space-y-3 text-sm opacity-70">
              <li data-testid="footer-address">{address}</li>
              <li><a data-testid="footer-phone" href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="hover:opacity-100">{phone}</a></li>
              <li><a data-testid="footer-email" href={`mailto:${mail}`} className="hover:opacity-100">{mail}</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-4 text-xs opacity-50">
          <p>© {new Date().getFullYear()} Classina® · A brand of SBK Manufacturing Co. All rights reserved.</p>
          <p>FSC-certified paper · Recyclable kraft · Made in India</p>
        </div>
      </div>
    </footer>
  );
};
